import axios from "axios";
import { BASE_URL } from ".";
import {
	UpdatePaperRequest,
	CreatePaperRequest,
	Paper,
	SectionSuggestionRequest,
	RephraseRequest,
	GenerateAbstractRequest,
	GenerateAbstractResponse,
	GenerateSummaryRequest,
	GenerateSummaryResponse,
	GenerateConclusionRequest,
	GenerateConclusionResponse,
	GenerateAimsRequest,
	GenerateAimsResponse,
} from "../types/Writer/Paper";

import {
	ReorganizedStructureNode,
	ReorganizeSectionResult
} from "../types/Writer/ReorganizeSectionResult";
import { PaperOutline, PaperOutlineNode } from "../types/Writer/OutlineNode";
import { RephraseSentencesResult } from "../types/Writer/RephraseSentencesResult";
import { RewriteSectionResult } from "../types/Writer/RewriteSectionResult";
import { BibContent } from "../types/Writer/BibContent";
import { AddLatexPackageParams, DeleteLatexPackageParams,UpdateLatexPackageParams, LatexListData  } from "../types/latexPackage";
import { PaperNodeSuggestion, PaperSuggestions } from "../types/Writer/PaperNodeSuggestion";

export interface Author {
  name: string;
  affiliation: string;
  email?: string;
  orcid?: string;
	order: number
}

export class PaperService {
    public static async getPapers() {
		const response = await axios.get<Paper[]>(BASE_URL + "/edit-api/paper");
		return response.data;
	}

	public static async getPaperById(paperId: string) {
		const response = await axios.get<Paper>(BASE_URL + "/edit-api/paper/" + paperId);
		return response.data;
	}

	public static async createPaper(data: CreatePaperRequest) {
		const response = await axios.post<Paper>(BASE_URL + "/edit-api/paper", data);
		return response.data;
	}

	public static async updatePaper(paperId: string, newPaper: UpdatePaperRequest) {
		const response = await axios.put<Paper>(BASE_URL + "/edit-api/paper/" + paperId, newPaper);
		return response.data;
	}

	public static async deletePaper(paperId: string): Promise<void> {
		await axios.delete(BASE_URL + "/edit-api/paper/" + paperId);
	}

	public static async rephraseSentences(data: RephraseRequest) {
		const response = await axios.post<RephraseSentencesResult>(BASE_URL + "/edit-api/paper/rephrase-sentences", data);
		return response.data;
	}

	public static async refineSentences(data: RephraseRequest) {
		const response = await axios.post<RephraseSentencesResult>(BASE_URL + "/edit-api/paper/touchup-sentences", data);
		return response.data;
	}

	public static async sectionSuggestion({ sectionNode, suggestion_data, suggestion_level, paperId }: SectionSuggestionRequest) {
		const response = await axios.post<PaperNodeSuggestion>(BASE_URL + "/edit-api/paper/rescore-suggestion", { sectionNode, suggestion_data,suggestion_level, paperId });
		return response.data;
	}

	public static async suggestion(sectionNode: PaperOutlineNode, suggestion_level: string, paperId: string) {
		const response = await axios.post<PaperSuggestions>(BASE_URL + "/edit-api/paper/suggestion", { sectionNode, suggestion_level, paperId });
		return response.data;
	}
	
	public static async rewriteSection(outline: PaperOutline, reorganizedStructure: ReorganizedStructureNode, writingLevel?: string, writingLength?: string, currentIndex?: number) {
		const response = await axios.post<RewriteSectionResult>(BASE_URL + "/edit-api/paper/rewrite-section", { outline, reorganizedStructure, writingLevel, writingLength, currentIndex });
		return response.data;
	}

	public static async reorganizeSection(sectionNode: PaperOutlineNode, writingLevel?: string, writingLength?: string) {
		const response = await axios.post<ReorganizeSectionResult>(BASE_URL + "/edit-api/paper/reorganize-section", { sectionNode, writingLevel, writingLength });
		return response.data;
	}

	public static async extractBibContent(payload: { paperId: string, bib_content: string }) {
		const { paperId, bib_content } = payload
		const response = await axios.post<BibContent[]>(BASE_URL + "/edit-api/paper/extract-bib", { paperId, bib_content });
		return response.data;
	}

	/**
	 * add custom bib
	 * @param payload
	 */
	public static async addCustomBib(payload: {paperId: string, bibList: BibContent[]}) {
		const { paperId, bibList } = payload
		const response = await axios.post<BibContent[]>(BASE_URL + "/edit-api/paper/add-custom-bib", { paperId, bibList });
		return response.data;
	}

	/**
	 * edit custom bib
	 * @param payload
	 */
	public static async editCustomBib(payload: {paperId: string, bibList: BibContent[]}) {
		const { paperId, bibList } = payload
		const response = await axios.post<BibContent[]>(BASE_URL + "/edit-api/paper/edit-bib", { paperId, bibList });
		return response.data;
	}

	/**
	 * delete custom bib
	 * @param payload
	 */
		public static async deleteCustomBib(payload: {paperId: string, bibDict: {bibID: string}}) {
			const { paperId, bibDict } = payload
			const response = await axios.post<BibContent[]>(BASE_URL + "/edit-api/paper/delete-custom-bib", { paperId, bibDict });
			return response.data;
		}

	/**
	 * 查询指定 paperId 下所有已添加的 LaTeX 包
	 */
	public static async getLatexPackages(paperId: string) {
		const response = await axios.get<LatexListData[]>(BASE_URL + "/edit-api/latex/list-latex-packages?paperId=" + paperId);
		return response.data;
	}

	public static async addLatexPackage(data: AddLatexPackageParams) {
		const response = await axios.post<LatexListData[]>(BASE_URL + "/edit-api/latex/add-latex-package", data );
		return response.data;
	}

	public static async deleteLatexList(params: DeleteLatexPackageParams) {
		const response = await axios.delete<null>(BASE_URL + "/edit-api/latex/delete-latex-package", { params });
		console.log('@@@@@',BASE_URL + "/edit-api/latex/delete-latex-package")
		return response.data;
	}

	public static async upadteLatexList(data: UpdateLatexPackageParams) {
		const response = await axios.put<LatexListData>(BASE_URL + "/edit-api/latex/edit-latex-package", data);
		return response.data;
	}

	/**
	 * 获取论文大纲历史记录
	 * @param paperId 
	 */
	public static async getPaperOutlineHistory(paperId: string) {
		const response = await axios.get(BASE_URL + "/edit-api/paper/" + paperId + "/outline/history/");
		return response.data;
	}

	/**
	 * 获取历史记录详情
	 * @param historyId 
	 */
	public static async getHistoryDetail(historyId: string) {
		const response = await axios.get(BASE_URL + "/edit-api/history/" + historyId + "/");
		return response.data;
	}

	/**
	 * 从历史版本恢复论文大纲
	 * @param paperId 
	 * @param data 
	 */
	public static async restorePaperOutline(paperId: string, data: { current_outline: any; target_history_id: string }) {
		const response = await axios.post(BASE_URL + "/edit-api/paper/" + paperId + "/outline/restore/", data);
		return response.data;
	}

	/**
	 * 列出指定 Paper 的所有作者信息
	 */
	public static async getPaperAuthors (paperId: string) {
		const response = await axios.get(BASE_URL + `/edit-api/paper/${paperId}/authors/`);
		return response.data;
	}

	/**
	 * 批量替换指定 Paper 的作者关系
	 */
	public static async savePaperAuthors (paperId: string, data: Author[]) {
		const response = await axios.post(BASE_URL + `/edit-api/paper/${paperId}/authors/bulk-replace/`, data);
		return response.data;
	}

	/**
	 * upload image
	 * @param paperId
	 * @param file
	 */
	public static async uploadImage(paperId: string, file: any) {
		const formData = new FormData();
		formData.append("file", file);
		const response = await axios.post(BASE_URL + `/edit-api/paper/${paperId}/images/`,
			formData,
			{
				headers: {
					"Content-Type": "multipart/form-data",
				}
			}
		);
		return response.data;
	}
	/**
	 * pdf compile
	 * @param payload
	 */
	public static async compilePdf(payload: {sectionNode: PaperOutlineNode, paperStyle: 'single' | 'double', paperId: string}) {
		const { sectionNode, paperStyle, paperId } = payload
		const response = await axios.post<{
			base64_pdf_string: string
			compile_message: string
		}>(BASE_URL + "/edit-api/paper/compile-section", { sectionNode, paperStyle, paperId });
		return response.data;
	}

	/**
	 * generate abstract
	 * @param data
	 */
	public static async generateAbstract(data: GenerateAbstractRequest) {
		const response = await axios.post<GenerateAbstractResponse>(BASE_URL + "/edit-api/paper/generate_abstract", data);
		return response.data;
	}

	/**
	 * generate summary
	 * @param data
	 */
	public static async generateSummary(data: GenerateSummaryRequest) {
		const response = await axios.post<GenerateSummaryResponse>(BASE_URL + "/edit-api/paper/generate_summary", data);
		return response.data;
	}

	/**
	 * generate conclusion
	 * @param data
	 */
	public static async generateConclusion(data: GenerateConclusionRequest) {
		const response = await axios.post<GenerateConclusionResponse>(BASE_URL + "/edit-api/paper/generate_conclusion", data);
		return response.data;
	}

	/**
	 * generate specific aims
	 * @param data
	 */
	public static async generateAims(data: GenerateAimsRequest) {
		const response = await axios.post<GenerateAimsResponse>(BASE_URL + "/edit-api/paper/generate_aims", data);
		return response.data;
	}
}
